import { intersection, set } from "@prismicio/editor-support/URLSearchParams"
import type { TeamSpace } from "editor-fields/src/core/service/role"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"

import { mainSpaceMeta } from "../../app/services/role"

/**
 * Creates new URLSearchParams for Team Space navigation.
 *
 * @param currentSearchParams - The current URL search parameters
 * @param teamSpaceId - The ID of the Team Space you want to create params for
 * @param teamSpaceCustomTypes - Array of Custom Type IDs allowed in the Team Space
 * you want to create params for
 */
export function getSearchParamsForTeamSpaceNavigation(
  currentSearchParams: URLSearchParams,
  teamSpaceId: string,
  teamSpaceCustomTypes: string[],
) {
  const newSearchParams = new URLSearchParams(currentSearchParams)
  const space = teamSpaceId === mainSpaceMeta.id ? undefined : teamSpaceId
  set(newSearchParams, "space", space)
  intersection(newSearchParams, "customTypes", teamSpaceCustomTypes)
  return newSearchParams
}

export function useTeamSpaceNavigation() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { pathname } = useLocation()

  return {
    /**
     * Navigates to a Team Space.
     * @param teamSpaceId - The ID of the Team Space you want to navigate to
     * @param teamSpaceCustomTypes - Array of Custom Type IDs allowed in the Team Space
     * you want to navigate to.
     */
    navigateToTeamSpace: (
      teamSpaceId: string,
      teamSpaceCustomTypes: string[],
    ) => {
      navigate({
        pathname,
        search: getSearchParamsForTeamSpaceNavigation(
          searchParams,
          teamSpaceId,
          teamSpaceCustomTypes,
        ).toString(),
      })
    },
  }
}

export function getTeamSpaceLabel(teamSpace: TeamSpace) {
  if (teamSpace.id === mainSpaceMeta.id) return mainSpaceMeta.name
  return `${teamSpace.name} Space`
}
